import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  FaCalendar, FaCamera, FaImages, FaMapMarkerAlt, FaRegImage, FaUserCircle,
} from 'react-icons/fa';
import { getImageUrl, getPhotographerProfile } from '../services/api';

const getPhotoDisplayName = (photo) => {
  const name = photo?.name || photo?.title || photo?.filename || `Photo ${photo?.id || ''}`;
  return name.split('/').pop() || name;
};

const getPhotoSource = (photo) => getImageUrl(photo?.filename || photo?.url);

function PhotographerProfile() {
  const { photographerId } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchProfile();
  }, [photographerId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchProfile = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await getPhotographerProfile(photographerId);
      setProfile(response);
    } catch (err) {
      setError(err.message || 'Impossible de charger le profil du photographe.');
    } finally {
      setLoading(false);
    }
  };

  const photographer = profile?.photographer;
  const displayName = photographer?.business_name || photographer?.name || 'Photographe';
  const topPhotos = useMemo(() => (profile?.photos || []).slice(0, 5), [profile]);
  const events = profile?.events || [];

  if (loading) {
    return (
      <div className="container" style={{ textAlign: 'center', padding: '4rem 1rem' }}>
        <div className="spinner"></div>
        <p style={{ marginTop: '1rem', color: '#64748B' }}>Chargement du photographe...</p>
      </div>
    );
  }

  if (error || !photographer) {
    return (
      <div className="container" style={{ padding: '4rem 1rem', textAlign: 'center' }}>
        <FaUserCircle style={{ fontSize: '4rem', color: '#64748B', marginBottom: '1rem' }} />
        <h2 style={{ color: '#1F2937', marginBottom: '0.75rem' }}>Photographe introuvable</h2>
        <p style={{ color: '#64748B', marginBottom: '1.5rem' }}>
          {error || "Cette page publique de photographe n'est pas disponible."}
        </p>
        <Link to="/events" className="btn-primary">Parcourir les événements</Link>
      </div>
    );
  }

  return (
    <div style={{ background: '#F7F8FC', minHeight: '100vh', padding: '2rem 0 4rem' }}>
      <div className="container" style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <section style={{
          background: '#FFFFFF',
          borderRadius: '14px',
          padding: '2rem',
          boxShadow: '0 2px 14px rgba(0,0,0,0.06)',
          marginBottom: '2rem',
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            gap: '1.5rem',
            alignItems: 'center',
          }}>
            <div style={{
              width: '112px',
              height: '112px',
              borderRadius: '50%',
              background: '#F7F8FC',
              overflow: 'hidden',
              display: 'grid',
              placeItems: 'center',
              color: '#64748B',
              fontSize: '3rem',
              flexShrink: 0,
            }}>
              {photographer.profile_photo ? (
                <img
                  src={getImageUrl(photographer.profile_photo)}
                  alt={displayName}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <FaUserCircle />
              )}
            </div>

            <div>
              <p style={{
                color: '#4F46E5',
                fontWeight: '700',
                textTransform: 'uppercase',
                fontSize: '0.8rem',
                margin: '0 0 0.35rem',
              }}>
                Photographe
              </p>
              <h1 style={{ color: '#1F2937', margin: '0 0 0.6rem', fontSize: '2.4rem' }}>
                {displayName}
              </h1>
              {photographer.business_name && photographer.name !== photographer.business_name && (
                <p style={{ color: '#64748B', margin: '0 0 0.75rem' }}>
                  {photographer.name}
                </p>
              )}
              {photographer.bio && (
                <p style={{ color: '#1F2937', lineHeight: 1.7, margin: 0, maxWidth: '780px' }}>
                  {photographer.bio}
                </p>
              )}
            </div>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            gap: '1rem',
            marginTop: '2rem',
          }}>
            <Stat icon={<FaImages />} label="Photos publiques" value={profile.stats?.photos_count || 0} color="#4F46E5" />
            <Stat icon={<FaCamera />} label="Événements publics" value={profile.stats?.events_count || 0} color="#4ADE80" />
            <Stat
              icon={<FaCalendar />}
              label="Membre depuis"
              value={new Date(photographer.created_at).getFullYear()}
              color="#C084FC"
            />
          </div>
        </section>

        <section style={{ marginBottom: '2rem' }}>
          <h2 style={{ color: '#1F2937', margin: '0 0 1rem' }}>Top 5 des photos</h2>
          {topPhotos.length === 0 ? (
            <div style={{
              background: '#FFFFFF',
              borderRadius: '14px',
              padding: '4rem 1rem',
              textAlign: 'center',
              color: '#64748B',
              boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
            }}>
              <FaImages style={{ fontSize: '3rem', color: '#64748B', marginBottom: '1rem' }} />
              <h3 style={{ color: '#1F2937', margin: '0 0 0.5rem' }}>Aucune photo publique pour le moment</h3>
              <p style={{ margin: 0 }}>Ce photographe n'a pas encore publié de photos d'événements publiques.</p>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
              gap: '1.25rem',
            }}>
              {topPhotos.map((photo) => (
                <article key={photo.id} style={{
                  background: '#FFFFFF',
                  borderRadius: '10px',
                  overflow: 'hidden',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
                }}>
                  <img
                    src={getPhotoSource(photo)}
                    alt={getPhotoDisplayName(photo)}
                    style={{ width: '100%', aspectRatio: '4 / 3', objectFit: 'cover', display: 'block' }}
                    onError={(e) => { e.currentTarget.src = 'https://placehold.co/440x330/png'; }}
                  />
                  <div style={{ padding: '0.85rem' }}>
                    <div
                      title={getPhotoDisplayName(photo)}
                      style={{
                        color: '#1F2937',
                        fontWeight: '700',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        marginBottom: '0.45rem',
                      }}
                    >
                      {getPhotoDisplayName(photo)}
                    </div>
                    <p style={{ color: '#64748B', margin: 0, fontSize: '0.9rem' }}>
                      {photo.event?.name || 'Événement public'}
                    </p>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>

        {events.length > 0 && (
          <section>
            <h2 style={{ color: '#1F2937', margin: '0 0 1rem' }}>Galeries publiques</h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
              gap: '1rem',
            }}>
              {events.map((event) => (
                <div key={event.id} style={{
                  background: '#FFFFFF',
                  borderRadius: '10px',
                  padding: '1rem',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                }}>
                  <h3 style={{ color: '#1F2937', margin: '0 0 0.75rem', fontSize: '1rem' }}>
                    {event.name}
                  </h3>
                  <div style={{ color: '#64748B', fontSize: '0.9rem', display: 'grid', gap: '0.45rem' }}>
                    {event.location && (
                      <span><FaMapMarkerAlt style={{ color: '#4F46E5', marginRight: '0.45rem' }} />{event.location}</span>
                    )}
                    {event.event_date && (
                      <span><FaCalendar style={{ color: '#4ADE80', marginRight: '0.45rem' }} />{new Date(event.event_date).toLocaleDateString()}</span>
                    )}
                    <span><FaRegImage style={{ color: '#C084FC', marginRight: '0.45rem' }} />{event.photos_count || 0} photos</span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

const Stat = ({ icon, label, value, color }) => (
  <div style={{
    border: '1px solid var(--border)',
    borderRadius: '10px',
    padding: '1rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.85rem',
  }}>
    <span style={{ color, fontSize: '1.35rem', display: 'flex' }}>{icon}</span>
    <span>
      <strong style={{ display: 'block', color: '#1F2937', fontSize: '1.25rem' }}>{value}</strong>
      <span style={{ color: '#64748B', fontSize: '0.9rem' }}>{label}</span>
    </span>
  </div>
);

export default PhotographerProfile;
